import { SiteUrls } from '@tableau/shared-urls';
import React from 'react';
import { defaultMemoize } from 'reselect';
import _ from 'underscore';

import { addContentTypeToProjectHierarchy, populateContentBreadcrumbs, } from '~src/app/content/breadcrumbs/BreadcrumbHelpers';
import { SpacesSiteUrls } from '~src/app/spaces/SpacesSiteUrls';
import { maybeSendTelemetry_favoriteItemOpened } from '~src/app/spaces/SpaceTelemetry';
import { explorerSubspace } from '~src/app/spaces/SubspaceHelpers';
import { Subspace, TabbedSpace } from '~src/app/spaces/TabbedSpace';
import { ISpaceInfoTypeItemCount } from '~src/app/state/ContentCountsState';
import { PublishedConnectionSubspaceName } from '~src/app/state/SpaceInfo';
import { Messages } from '~src/codegen/Localize';
import { PublishedConnectionTableDefinition } from '~src/contentDefinitions/PublishedConnectionTableDefinition';
import { FeatureFlags } from '~src/ts/FeatureFlags';
import { IPublishedConnectionDetailInfo, IPublishedConnectionInfo } from '~src/ts/models/IPublishedConnectionInfo';
import { INamedItemInfo } from '~src/ts/models/resourceInfoParts';
import { IPublishedConnectionActions } from '~src/ts/ServerApi/types/IPublishedConnectionActions';

import { PublishedConnections } from '../resources/PublishedConnections';
import { PublishedConnectionSubplaceProps } from '../types/PublishedConnectionSubplaceProps';
import { PublishedConnectionScheduledTasksSubplace } from './PublishedConnectionScheduledTasksSubplace';
import { PublishedConnectionSpaceHeader } from './PublishedConnectionSpaceHeader';

export type PublishedConnectionSpaceStateProps = Readonly<{
  contentCountsPublishedConnection: ISpaceInfoTypeItemCount<'publishedConnection'>;
  contentCountsUpToDate: boolean;
  isServerOrSiteAdmin: boolean;
  urls: SpacesSiteUrls;
  catalogingOnSiteEnabled: boolean;
  areUsersVisible: boolean;
}>;

export type PublishedConnectionSpaceDispatchProps = Readonly<{
  addToFavorites: () => void;
  enterSpace: (requestContent: boolean) => void;
  fetchUpdatedContentCounts: () => void;
  markContentCountsUpToDate: () => void;
  removeFromFavorites: () => void;
}>;

export type PublishedConnectionSpaceOwnProps = Readonly<{
  publishedConnectionAllowedActions: IPublishedConnectionActions;
  publishedConnectionDetailInfo: IPublishedConnectionDetailInfo;
  publishedConnectionInfo: IPublishedConnectionInfo;
  projectHierarchy: INamedItemInfo[];
  refreshData: () => void;

  // subplace info
  readonly subplaceName: string;
  readonly siteUrls: SiteUrls;
}>;

export type PublishedConnectionSpaceProps =
  PublishedConnectionSpaceStateProps
  & PublishedConnectionSpaceDispatchProps
  & PublishedConnectionSpaceOwnProps;

function tablesSubspace(subplaceProps: PublishedConnectionSubplaceProps): Subspace {
  return explorerSubspace({
    contentCount: getContentCount(subplaceProps.contentCountsPublishedConnection, 'tables'),
    contentCountUpToDate: subplaceProps.contentCountsUpToDate,
    emptyStateProps: {
      primaryMessage: Messages.publishedConnectionTablesEmpty()
    },
    hideSelection: false,
    name: 'tables',
    shouldRenderContentCount: true,
    supportedDisplayModes: PublishedConnectionTableDefinition.supportedDisplayModes,
    supportFilterPanel: false,
    tabLabel: Messages.publishedConnectionTablesTabHeader(),
    toolbarVisibility: 'enabled',
  });
}

function scheduledTasksSubspace(subplaceProps: PublishedConnectionSubplaceProps): Subspace {

  return {
    name: 'scheduledTasks',
    renderContent: () => (
      <PublishedConnectionScheduledTasksSubplace
        editActionsAllowed={subplaceProps.publishedConnectionAllowedActions.refreshExtract}
        isServerOrSiteAdmin={subplaceProps.isServerOrSiteAdmin}
        publishedConnectionDetailInfo={subplaceProps.publishedConnectionDetailInfo}
        updateScheduledTasksCount={subplaceProps.fetchUpdatedContentCounts}
      />
    ),
    shouldRenderTabContentCount: true,
    tabContentCount: getContentCount(subplaceProps.contentCountsPublishedConnection, 'scheduledTasks'),
    tabContentCountUpToDate: subplaceProps.contentCountsUpToDate,
    tabLabel: Messages.publishedConnectionScheduledTasksTabHeader(),
  };
}

function lineageSubspace(
  publishedConnection: IPublishedConnectionDetailInfo,
  siteUrls: SiteUrls,
  areUsersVisible: boolean
): Subspace {
  return {
    name: 'lineage',
    tabLabel: Messages.dataCatalogLineage(),
    shouldRenderTabContentCount: false,
    renderContent: () =>
      <div/>
  };
}

function getPublishedConnectionDetailWithUsage(
  publishedConnectionDetailInfo: IPublishedConnectionDetailInfo,
  publishedConnectionInfo: IPublishedConnectionInfo,
) {
  return {
    usageInfo: publishedConnectionInfo.usageInfo,
    usageInfoMaxValues: publishedConnectionInfo.usageInfoMaxValues,
    ...publishedConnectionDetailInfo,
  };
}

function getPublishedConnectionSubspaces(subplaceProps: PublishedConnectionSubplaceProps): ReadonlyArray<Subspace> {
  return _.compact<_.List<Subspace | false>>([
    tablesSubspace(subplaceProps),
    FeatureFlags.publishedConnectionExtractSchedulingEnabled
    && PublishedConnections.hasExtracts(subplaceProps.publishedConnectionDetailInfo)
    && scheduledTasksSubspace(subplaceProps),
    FeatureFlags.publishedConnectionsLineageEnabled &&
      subplaceProps.catalogingOnSiteEnabled &&
      lineageSubspace(subplaceProps.publishedConnectionDetailInfo, subplaceProps.siteUrls, subplaceProps.areUsersVisible),
  ]);
}

function getContentCount(contentCountsPublishedConnection: ISpaceInfoTypeItemCount<'publishedConnection'>, contentName: string): number {
  return contentCountsPublishedConnection && contentCountsPublishedConnection[contentName as PublishedConnectionSubspaceName];
}

export class PublishedConnectionSpace extends React.PureComponent<PublishedConnectionSpaceProps> {
  private memoizedPublishedConnectionDetailWithUsage = defaultMemoize(getPublishedConnectionDetailWithUsage);
  private memoizedGetPublishedConnectionSubspaces = defaultMemoize(getPublishedConnectionSubspaces);

  public urlFromSubspaceName = (subspaceName?: PublishedConnectionSubspaceName) => {
    switch (subspaceName) {
      case 'tables':
        return this.props.urls.publishedConnectionOverviewUrl(this.props.publishedConnectionDetailInfo.id);
      case 'scheduledTasks':
        return this.props.urls.publishedConnectionScheduledTasksUrl(this.props.publishedConnectionDetailInfo.id);
      case 'lineage':
        return this.props.urls.publishedConnectionLineageUrl(this.props.publishedConnectionDetailInfo.id);
    }
  };

  public render() {
    const subplaceProps: PublishedConnectionSubplaceProps = {
      contentCountsPublishedConnection: this.props.contentCountsPublishedConnection,
      contentCountsUpToDate: this.props.contentCountsUpToDate,
      fetchUpdatedContentCounts: this.props.fetchUpdatedContentCounts,
      isServerOrSiteAdmin: this.props.isServerOrSiteAdmin,
      publishedConnectionAllowedActions: this.props.publishedConnectionAllowedActions,
      publishedConnectionDetailInfo: this.props.publishedConnectionDetailInfo,
      refreshData: this.props.refreshData,
      siteUrls: this.props.urls,
      areUsersVisible: this.props.areUsersVisible,
      catalogingOnSiteEnabled: this.props.catalogingOnSiteEnabled
    };

    const subspaces = this.memoizedGetPublishedConnectionSubspaces(subplaceProps);

    return (
      <TabbedSpace
        subspaces={subspaces}
        activeSubspaceName={this.props.subplaceName as PublishedConnectionSubspaceName}
        urlForSubspace={this.urlFromSubspaceName}
        mastheadElement={this.mastheadElement()}
        testIdPrefix={'publishedConnection'}
        spaceItemName={this.props.publishedConnectionDetailInfo.name}
      />
    );
  }

  public componentDidMount() {
    maybeSendTelemetry_favoriteItemOpened(this.props.publishedConnectionDetailInfo);
    this.changeSubspace(true);
    this.updateBreadcrumbs();
  }

  public componentDidUpdate(prevProps: PublishedConnectionSpaceProps) {
    if (this.props.publishedConnectionDetailInfo.id !== prevProps.publishedConnectionDetailInfo.id) {
      maybeSendTelemetry_favoriteItemOpened(this.props.publishedConnectionDetailInfo);
      this.changeSubspace(true);
      this.updateBreadcrumbs();
    } else if (this.props.subplaceName !== prevProps.subplaceName) {
      this.changeSubspace(false);
    }
  }

  private shouldRequestContent() {
    // tasks are fetched from within the component, so no need to request via mixed content action
    switch (this.props.subplaceName) {
      case 'tables':
        return true;
      case 'scheduledTasks':
        return false;
    }
  }

  private changeSubspace(didPublishedConnectionChange: boolean) {
    this.props.enterSpace(this.shouldRequestContent());
    if (didPublishedConnectionChange) {
      this.props.fetchUpdatedContentCounts();
    } else {
      // enterSpace action causes counts out of date.
      // We mark it upto date because there is no need to fetch count again for same published connection.
      this.props.markContentCountsUpToDate();
    }
  }

  private updateBreadcrumbs() {
    const projectHierarchyWithType = addContentTypeToProjectHierarchy(this.props.projectHierarchy);
    populateContentBreadcrumbs([...projectHierarchyWithType, this.props.publishedConnectionDetailInfo], this.props.urls);
  }

  private mastheadElement(): React.ReactNode {
    const publishedConnectionDetailWithUsage = this.memoizedPublishedConnectionDetailWithUsage(
      this.props.publishedConnectionDetailInfo,
      this.props.publishedConnectionInfo,
    );

    return (
      <PublishedConnectionSpaceHeader
        itemInfo={publishedConnectionDetailWithUsage}
        availableActions={this.props.publishedConnectionAllowedActions}
        openDetail={() => null}
        addToFavorites={this.props.addToFavorites}
        removeFromFavorites={this.props.removeFromFavorites}
        urls={this.props.urls}
      />
    );
  }
}
